import { isStandardRole, useUserRoleForCurrentWorkspace } from '@/features/roles/hook';
import { authStore } from '@/services/auth/store';
import { getMasqBarVisibility, setMasqBarVisibility } from '@/utils/masq';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Role } from '@customerio/hydra-permissions/roles';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Image,
  PlainButton,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { getWorkspaceSettingsLink } from '@/hooks/get-workspace-settings-link';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';
import santaHat from '@/assets/santa.png';
import dreidel from '@/assets/dreidel.png';
import kinara from '@/assets/kinara.png';

type HolidayIcon = 'santa' | 'dreidel' | 'kinara' | 'none';

const HOLIDAY_ICON_STORAGE_KEY = 'holiday-icon-selection';

function currentUserRoleString(role: string) {
  const roleStrings: Record<Role, string> = {
    [Role.Admin]: 'an Account admin',
    [Role.Manager]: 'a Workspace admin',
    [Role.Author]: 'an Author',
    [Role.AuthorRedacted]: 'an Author',
    [Role.AuthorRedactedSensitive]: 'an Author',
    [Role.AuthorRedactedAll]: 'an Author',
    [Role.Viewer]: 'a Viewer',
    [Role.ViewerRedacted]: 'a Viewer',
    [Role.ViewerRedactedSensitive]: 'a Viewer',
    [Role.ViewerRedactedAll]: 'a Viewer',
  };

  if (isStandardRole(role)) {
    return `You're ${roleStrings[role]}`;
  }

  return role;
}

export function PersonDropdown() {
  const auth = useStore(authStore);
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const workspaceSettingsLink = getWorkspaceSettingsLink();
  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();

  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  const isSetupGuideIncomplete = !isSetupGuideLoading && setupGuide.completed_at == null;

  const showSetUpWorkspaceNavItem =
    isSetupGuideIncomplete &&
    window.sessionStorage.getItem('showSetUpWorkspaceNavItem') === 'true' &&
    !isSampleWorkspace(currentWorkspace?.id);

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(getMasqBarVisibility);
  const [selectedHolidayIcon, setSelectedHolidayIcon] = useState<HolidayIcon>(() => {
    try {
      const savedIcon = localStorage.getItem(HOLIDAY_ICON_STORAGE_KEY) as HolidayIcon | null;
      if (savedIcon != null && ['santa', 'dreidel', 'kinara', 'none'].includes(savedIcon)) {
        return savedIcon;
      }
    } catch {
      // ignore
    }
    return 'santa';
  });

  const onHolidayIconSelect = (icon: HolidayIcon) => {
    try {
      localStorage.setItem(HOLIDAY_ICON_STORAGE_KEY, icon);
      setSelectedHolidayIcon(icon);
    } catch {
      // ignore
    }
  };

  const onMasqueradeChange = (event: React.FormEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newValue = !isShowingMasqBar;
    setMasqBarVisibility(newValue);
    setIsShowingMasqBar(newValue);

    // If turning off masq bar, remove masq_account_id from URL
    if (!newValue) {
      const url = new URL(window.location.href);
      url.searchParams.delete('masq_account_id');
      window.location.href = url.toString();
    } else {
      // Reload the page, so that the Journeys UI picks up the local storage update
      window.location.reload();
    }
  };

  return (
    <>
      <DropdownMenu>
        {(context) => (
          <IconButton
            label="Menu"
            icon="manage"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            {selectedHolidayIcon === 'santa' ? (
              <Image
                position="absolute"
                style={{
                  top: '-2px',
                  right: '0px',
                  pointerEvents: 'none',
                }}
                src={santaHat}
                alt=""
                width={24}
                height={24}
              />
            ) : selectedHolidayIcon === 'dreidel' ? (
              <Image
                position="absolute"
                style={{
                  bottom: '-2px',
                  right: '-2px',
                  pointerEvents: 'none',
                  transform: 'rotate(15deg)',
                }}
                src={dreidel}
                alt=""
                width={22}
                height={22}
              />
            ) : selectedHolidayIcon === 'kinara' ? (
              <Image
                position="absolute"
                style={{
                  top: '6px',
                  right: '-4px',
                  pointerEvents: 'none',
                }}
                src={kinara}
                alt=""
                width={22}
                height={22}
              />
            ) : null}
          </IconButton>
        )}
        <DropdownMenuContent>
          {userRole != null || showSetUpWorkspaceNavItem ? (
            <DropdownMenuGroup>
              {userRole != null ? (
                <DropdownMenuItem>
                  {currentUserRoleString(userRole.name)}
                  {userRole.redacted ? (
                    <div style={{ width: '20px', height: '20px', marginLeft: '8px' }}>
                      <Icon name="view-off" size="fill" display="block" />
                    </div>
                  ) : null}
                </DropdownMenuItem>
              ) : null}
              {showSetUpWorkspaceNavItem ? (
                <DropdownMenuItem href={`/workspaces/${currentWorkspace?.id}/journeys/home/workspace-setup`}>
                  Set up workspace
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
          ) : null}

          <DropdownMenuGroup>
            <DropdownMenuItem href="/settings/personal">Personal settings</DropdownMenuItem>
            <DropdownMenuItem href="/settings/experimental-features">Experimental features</DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem href="/settings">Account settings</DropdownMenuItem>
            <DropdownMenuItem href="/settings/privacy">Privacy, Data &amp; AI</DropdownMenuItem>
            {hasPermission('account/billing/view') === true ? (
              <DropdownMenuItem href="/settings/billing/your-plan">Plans &amp; Billing</DropdownMenuItem>
            ) : null}
            <DropdownMenuItem href={workspaceSettingsLink(currentWorkspace?.id ?? 'last')}>
              Workspace settings
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem>
              <Box display="flex" gap="200" alignItems="center" justifyContent="space-between" w="full">
                <Text size="s">Icon:</Text>
                <Box display="flex" gap="100" alignItems="center">
                  <PlainButton
                    onClick={() => onHolidayIconSelect('santa')}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    aria-label="Santa hat"
                    style={{
                      width: '32px',
                      height: '32px',
                      border:
                        selectedHolidayIcon === 'santa'
                          ? `${themeVars.border['width-025']} solid currentColor`
                          : `${themeVars.border['width-025']} solid transparent`,
                      borderRadius: themeVars.radius['4'],
                    }}
                  >
                    <Image src={santaHat} alt="" width={24} height={24} />
                  </PlainButton>
                  <PlainButton
                    onClick={() => onHolidayIconSelect('dreidel')}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    aria-label="Dreidel"
                    style={{
                      width: '32px',
                      height: '32px',
                      border:
                        selectedHolidayIcon === 'dreidel'
                          ? `${themeVars.border['width-025']} solid currentColor`
                          : `${themeVars.border['width-025']} solid transparent`,
                      borderRadius: themeVars.radius['4'],
                    }}
                  >
                    <Image src={dreidel} alt="" width={24} height={24} />
                  </PlainButton>
                  <PlainButton
                    onClick={() => onHolidayIconSelect('kinara')}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    aria-label="Kinara"
                    style={{
                      width: '32px',
                      height: '32px',
                      border:
                        selectedHolidayIcon === 'kinara'
                          ? `${themeVars.border['width-025']} solid currentColor`
                          : `${themeVars.border['width-025']} solid transparent`,
                      borderRadius: themeVars.radius['4'],
                    }}
                  >
                    <Image src={kinara} alt="" width={24} height={24} />
                  </PlainButton>
                  <PlainButton
                    onClick={() => onHolidayIconSelect('none')}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    aria-label="No icon"
                    style={{
                      width: '32px',
                      height: '32px',
                      border:
                        selectedHolidayIcon === 'none'
                          ? `${themeVars.border['width-025']} solid currentColor`
                          : `${themeVars.border['width-025']} dashed ${themeVars.color['border-base']}`,
                      borderRadius: themeVars.radius['4'],
                    }}
                  />
                </Box>
              </Box>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          {showAdminMasqueradeOption ? (
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Box display="flex" alignItems="center">
                  <Text
                    mr="400"
                    display="inline"
                    as="label"
                    id="toggle-admin-masq-label"
                    htmlFor="toggle-admin-masq"
                    textTransform="capitalize"
                  >
                    Admin masquerade
                  </Text>
                  <Toggle
                    size="sm"
                    labelOn="On"
                    labelOff="Off"
                    onChange={(event) => onMasqueradeChange(event)}
                    ariaLabelledby="toggle-admin-masq-label"
                    id="toggle-admin-masq"
                    isChecked={isShowingMasqBar}
                  />
                </Box>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          ) : null}
          <DropdownMenuGroup>
            <DropdownMenuItem href="/logout" isDanger={true}>
              Log out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
